import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import WhiteLogo from "../../assets/images/white-logo.svg";
import Logo from "../../assets/images/logo.svg";
import ImgPhone from "../../assets/images/login/mobiles.svg";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination } from "swiper/modules";
import Loader from "../../components/common/Loader";
import {
    requestPasswordOtp,
    verifyPasswordOtp,
    resetPasswordWithOtp,
} from "../../Services/authService";
import { checkPasswordRules } from "../../utils/passwordValidator";
import PasswordStrengthChecklist from "../../components/common/PasswordStrengthChecklist";

export const ForgotPassword = () => {
    const navigate = useNavigate();

    // Multi-step State (1: Email, 2: OTP, 3: New Password, 4: Success)
    const [step, setStep] = useState(1);

    // Form Fields
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    // UI Toggles & States
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [passwordFieldError, setPasswordFieldError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [successMsg, setSuccessMsg] = useState(null);
    const [countdown, setCountdown] = useState(0);

    // 60-second cooldown timer for resending OTP
    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    // Password Validation Checklist Rules
    const passwordRules = checkPasswordRules(password, { email });
    const isPasswordValid = passwordRules.isValid;

    // Email validation
    const validateEmail = (val) => {
        if (!val.trim()) {
            return "Email is required";
        }
        if (!/\S+@\S+\.\S+/.test(val)) {
            return "Please enter a valid email address";
        }
        return "";
    };

    // -------------------------------------------------------------
    // STEP 1: Request OTP
    // -------------------------------------------------------------
    const handleRequestOtp = async (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        setError(null);

        const vError = validateEmail(email);
        if (vError) {
            setEmailError(vError);
            return;
        }

        setLoading(true);
        try {
            await requestPasswordOtp(email.trim());
            setStep(2);
            setCountdown(60); // start 60s cooldown
            setError(null);
        } catch (err) {
            const resData = err.response?.data;
            let msg = resData?.message || resData?.msg || err.message || "Failed to send OTP.";
            if (err.response?.status === 429 || msg.includes("Too many OTP attempts") || msg.includes("Too many attempts")) {
                msg = "Too many OTP attempts. Please try again after 15 minutes.";
            }
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    // -------------------------------------------------------------
    // STEP 2: Verify OTP
    // -------------------------------------------------------------
    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setError(null);

        if (otp.length !== 6) {
            setError("Please enter the complete 6-digit verification code.");
            return;
        }

        setLoading(true);
        try {
            await verifyPasswordOtp(email.trim(), otp.trim());
            setStep(3);
            setError(null);
        } catch (err) {
            const resData = err.response?.data;
            let msg = resData?.msg || resData?.message || err.message || "Invalid OTP entered.";

            if (msg.includes("Invalid or expired password reset token or OTP") || msg.includes("Invalid OTP")) {
                msg = "Incorrect OTP. Please check your email and try again.";
            }

            setError(msg);

            // If user exceeded 5 failed attempts, return to Step 1 after 3 seconds
            if (
                msg.includes("Too many failed OTP verification attempts") ||
                (resData?.msg && resData.msg.includes("Too many failed OTP verification attempts"))
            ) {
                setTimeout(() => {
                    setStep(1);
                    setOtp("");
                    setError("Session expired due to too many attempts. Please request a new OTP.");
                }, 3000);
            }
        } finally {
            setLoading(false);
        }
    };

    // -------------------------------------------------------------
    // STEP 3: Reset Password
    // -------------------------------------------------------------
    const handleResetPassword = async (e) => {
        e.preventDefault();
        setPasswordFieldError("");
        setError(null);

        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        if (!isPasswordValid) {
            setError("Please satisfy all password complexity rules.");
            return;
        }

        setLoading(true);
        try {
            await resetPasswordWithOtp({
                email: email.trim(),
                otp: otp.trim(),
                password,
                confirmPassword,
            });

            setSuccessMsg("Password reset successfully! Redirecting to login...");
            setTimeout(() => {
                navigate("/login");
            }, 2500);
        } catch (err) {
            const resData = err.response?.data;
            let msg =
                resData?.errors?.password ||
                resData?.errors?.confirmPassword ||
                resData?.msg ||
                resData?.message ||
                err.message ||
                "Failed to reset password.";

            if (
                msg.includes("Cannot use a recently used password") ||
                msg.includes("recently used password") ||
                msg.includes("cannot be the same") ||
                msg.includes("reuse")
            ) {
                setPasswordFieldError(msg);
                setError(null);
            } else {
                setError(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {loading && <Loader />}

            <div className="login-page">
                {/* Left Section with Carousel */}
                <div className="left-section">
                    <div className="custome-coursole">
                        <Swiper pagination={true} modules={[Pagination]} className="mySwiper">
                            <SwiperSlide>
                                <div className="coursol-details">
                                    <div className="coursole-img">
                                        <img src={ImgPhone} alt="Phone Illustration" />
                                    </div>
                                    <div className="coursole-para">
                                        <h5>Secure Hospital Management</h5>
                                        <p>Fast, reliable, and compliant healthcare data management at your fingertips.</p>
                                    </div>
                                </div>
                            </SwiperSlide>
                            <SwiperSlide>
                                <div className="coursol-details">
                                    <div className="coursole-img">
                                        <img src={ImgPhone} alt="Phone Illustration" />
                                    </div>
                                    <div className="coursole-para">
                                        <h5>Protected Access</h5>
                                        <p>Multi-layered security protocols ensure patient and provider records stay safe.</p>
                                    </div>
                                </div>
                            </SwiperSlide>
                        </Swiper>
                    </div>
                    <div className="white-logo">
                        <img src={WhiteLogo} alt="Pran Health Logo" />
                    </div>
                </div>

                {/* Right Section with 3-Step Flow */}
                <div className="right-section">
                    <div className="logo-section-login">
                        <img src={Logo} alt="Hospital Logo" />
                    </div>

                    <div className="auth-flow-container">
                        {/* 3-Step Visual Progress Bar */}
                        <div className="auth-step-progress">
                            <div className={`step-item ${step === 1 ? "active" : step > 1 ? "completed" : ""}`}>
                                <span className="step-circle">
                                    {step > 1 ? "✓" : "1"}
                                </span>
                                <span>Email</span>
                            </div>
                            <span className="step-divider">➔</span>
                            <div className={`step-item ${step === 2 ? "active" : step > 2 ? "completed" : ""}`}>
                                <span className="step-circle">
                                    {step > 2 ? "✓" : "2"}
                                </span>
                                <span>Verify OTP</span>
                            </div>
                            <span className="step-divider">➔</span>
                            <div className={`step-item ${step === 3 ? "active" : ""}`}>
                                <span className="step-circle">3</span>
                                <span>New Password</span>
                            </div>
                        </div>

                        {/* Error Alert */}
                        {error && (
                            <div className="error-message mb-3" role="alert">
                                {error}
                            </div>
                        )}

                        {/* Success Notice / Redirecting */}
                        {successMsg ? (
                            <div className="auth-card-notice">
                                <div className="notice-icon-wrapper success">
                                    <i className="material-icons">check_circle</i>
                                </div>
                                <h3>Password Reset Complete</h3>
                                <p className="mb-3">{successMsg}</p>
                                <div className="d-flex justify-content-center">
                                    <div className="btn-spinner" style={{ borderColor: "#1429BB", borderTopColor: "transparent", width: 24, height: 24 }} />
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* =======================================================
                                    STEP 1: Enter Email
                                   ======================================================= */}
                                {step === 1 && (
                                    <div className="login-section" style={{ width: "100%" }}>
                                        <div className="auth-flow-header">
                                            <h4>Forgot Password</h4>
                                            <p>Enter your registered hospital email to receive a 6-digit verification code.</p>
                                        </div>

                                        <form onSubmit={handleRequestOtp} noValidate>
                                            <div className="loginFeilds">
                                                <div className="feilds">
                                                    <label htmlFor="forgot-email">Hospital Email Address</label>
                                                    <input
                                                        id="forgot-email"
                                                        type="email"
                                                        name="email"
                                                        autoComplete="email"
                                                        autoFocus
                                                        value={email}
                                                        onChange={(e) => {
                                                            setEmail(e.target.value);
                                                            if (emailError) setEmailError("");
                                                            if (error) setError(null);
                                                        }}
                                                        placeholder="e.g. doctor@hospital.com"
                                                        className={emailError ? "error" : ""}
                                                        required
                                                    />
                                                    {emailError && <small>{emailError}</small>}
                                                </div>

                                                <div className="feilds mt-3">
                                                    <button
                                                        type="submit"
                                                        className="customeBtn successBtn w-100 px-3 py-2"
                                                        disabled={loading}
                                                    >
                                                        {loading ? "Sending Code..." : "Send Verification Code"}
                                                    </button>
                                                </div>

                                                <div className="back-to-login">
                                                    <Link to="/login">
                                                        <i className="material-icons">arrow_back</i> Back to Login
                                                    </Link>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                )}

                                {/* =======================================================
                                    STEP 2: Enter 6-Digit OTP
                                   ======================================================= */}
                                {step === 2 && (
                                    <div className="login-section" style={{ width: "100%" }}>
                                        <div className="auth-flow-header">
                                            <h4>Verify 6-Digit Code</h4>
                                            <p>
                                                Enter the OTP sent to <strong>{email}</strong>.<br />
                                                <span className="text-muted small">(Valid for 15 minutes)</span>
                                            </p>
                                        </div>

                                        <form onSubmit={handleVerifyOtp} noValidate>
                                            <div className="loginFeilds">
                                                <div className="feilds">
                                                    <label htmlFor="otp-input">6-Digit Verification Code</label>
                                                    <input
                                                        id="otp-input"
                                                        type="text"
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                        maxLength={6}
                                                        required
                                                        autoFocus
                                                        className="form-control otp-input-field"
                                                        placeholder="••••••"
                                                        value={otp}
                                                        onChange={(e) => {
                                                            const val = e.target.value.replace(/\D/g, "");
                                                            setOtp(val);
                                                            if (error) setError(null);
                                                        }}
                                                    />
                                                </div>

                                                <div className="feilds mt-3">
                                                    <button
                                                        type="submit"
                                                        className="customeBtn successBtn w-100 px-3 py-2"
                                                        disabled={loading || otp.length !== 6}
                                                    >
                                                        {loading ? "Verifying..." : "Verify Code"}
                                                    </button>
                                                </div>

                                                <div className="otp-action-row">
                                                    <button
                                                        type="button"
                                                        className="btn-resend-otp"
                                                        disabled={countdown > 0 || loading}
                                                        onClick={handleRequestOtp}
                                                    >
                                                        {countdown > 0 ? `Resend OTP in ${countdown}s` : "Resend OTP"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn-change-email"
                                                        onClick={() => {
                                                            setStep(1);
                                                            setError(null);
                                                        }}
                                                    >
                                                        Change Email
                                                    </button>
                                                </div>

                                                <div className="back-to-login">
                                                    <Link to="/login">
                                                        <i className="material-icons">arrow_back</i> Back to Login
                                                    </Link>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                )}

                                {/* =======================================================
                                    STEP 3: Set New Password
                                   ======================================================= */}
                                {step === 3 && (
                                    <div className="login-section" style={{ width: "100%" }}>
                                        <div className="auth-flow-header">
                                            <h4>Set New Password</h4>
                                            <p>Create a strong password to secure your account.</p>
                                        </div>

                                        <form onSubmit={handleResetPassword} noValidate>
                                            <div className="loginFeilds">
                                                <div className="feilds">
                                                    <label htmlFor="new-password">New Password</label>
                                                    <div className="password-input-group">
                                                        <input
                                                            id="new-password"
                                                            type={showPassword ? "text" : "password"}
                                                            value={password}
                                                            onChange={(e) => {
                                                                setPassword(e.target.value);
                                                                if (error) setError(null);
                                                                if (passwordFieldError) setPasswordFieldError("");
                                                            }}
                                                            placeholder="Enter new password"
                                                            autoFocus
                                                            required
                                                            className={passwordFieldError ? "error" : ""}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="password-toggle-btn"
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                                        >
                                                            <i className="material-icons">
                                                                {showPassword ? "visibility_off" : "visibility"}
                                                            </i>
                                                        </button>
                                                    </div>
                                                    {passwordFieldError && (
                                                        <small className="text-danger mt-1 d-block" style={{ fontSize: "12px", fontWeight: 500 }}>
                                                            {passwordFieldError}
                                                        </small>
                                                    )}
                                                </div>

                                                {/* Live Password Requirements Checklist */}
                                                <PasswordStrengthChecklist rules={passwordRules} title="Password must contain:" />

                                                <div className="feilds">
                                                    <label htmlFor="confirm-password">Confirm New Password</label>
                                                    <div className="password-input-group">
                                                        <input
                                                            id="confirm-password"
                                                            type={showConfirmPassword ? "text" : "password"}
                                                            value={confirmPassword}
                                                            onChange={(e) => {
                                                                setConfirmPassword(e.target.value);
                                                                if (error) setError(null);
                                                            }}
                                                            placeholder="Confirm new password"
                                                            required
                                                        />
                                                        <button
                                                            type="button"
                                                            className="password-toggle-btn"
                                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                                        >
                                                            <i className="material-icons">
                                                                {showConfirmPassword ? "visibility_off" : "visibility"}
                                                            </i>
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="feilds mt-3">
                                                    <button
                                                        type="submit"
                                                        className="customeBtn successBtn w-100 px-3 py-2"
                                                        disabled={loading || !isPasswordValid || !confirmPassword || password !== confirmPassword}
                                                    >
                                                        {loading ? "Updating Password..." : "Update Password & Login"}
                                                    </button>
                                                </div>

                                                <div className="back-to-login">
                                                    <Link to="/login">
                                                        <i className="material-icons">arrow_back</i> Back to Login
                                                    </Link>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ForgotPassword;

